import type {
  TelemetryLocation,
  TelemetrySelectedProduct,
} from './TelemetryEvent.types';

export const telemetryLocationFromInput = (
  input?: TelemetryLocationInput,
): TelemetryLocation => ({
  id: input?.id ?? input?.googlePlaceId ?? '',
  name: input?.name ?? '',
  city: input?.city ?? '',
  state: input?.state ?? '',
  zip: input?.zipCode ?? '',
});

export const telemetryProductFromInput = (
  input?: TelemetryProductInput,
  modifications?: TelemetryIngredientModifications,
): TelemetrySelectedProduct => ({
  id: input?.id ?? '',
  restaurantId: input?.restaurantId ?? '',
  location: input?.location ?? '',
  category: input?.categoryId ?? '',
  name: input?.name ?? '',
  calories: input?.calories ?? 0,
  cost: Number(((input?.cost ?? 0) / 100).toFixed(2)),
  quantity: input?.quantity ?? 0,
  featured: input?.featured ?? false,
  bread: input?.bread ?? false,
  dressingStyle: input?.dressingStyle ?? '',
  tags: input?.tags ?? [],
  customized: isTelemetryProductCustomized(modifications),
  additions:
    modifications?.additions?.map(({ ingredientId }) => ingredientId ?? '') ??
    [],
  removals:
    modifications?.removals?.map(({ ingredientId }) => ingredientId ?? '') ??
    [],
  ingredients:
    input?.ingredients
      ?.map((ingredient) => ingredient?.name ?? '')
      .filter(Boolean) ?? [],
});

export const isTelemetryProductCustomized = (
  modifications?: TelemetryIngredientModifications,
): boolean => {
  return (
    (modifications?.additions?.length ?? 0) > 0 ||
    (modifications?.removals?.length ?? 0) > 0 ||
    (modifications?.substitutions?.length ?? 0) > 0
  );
};

type TelemetryIngredientModifications = {
  readonly additions: readonly IngredientModificationInput[];
  readonly removals: readonly IngredientModificationInput[];
  readonly substitutions: readonly IngredientSubstitutionModificationInput[];
};

type Ingredient = {
  readonly asset?: { readonly url?: string };
  readonly dietaryProperties?: readonly DietaryProperty[];
  readonly id?: string;
  readonly kind?: IngredientKind | null;
  readonly name?: string;
};

type DietaryProperty = {
  readonly id?: string;
  readonly name?: DietaryPropertyKind;
};

type DietaryPropertyKind =
  | 'DAIRY'
  | 'FISH'
  | 'GLUTEN'
  | 'MEAT'
  | 'NUTS'
  | 'SOY';

type IngredientKind = 'BASE' | 'DRESSING' | 'PREMIUM' | 'TOPPING';

type IngredientModificationInput = {
  readonly ingredientId?: string;
};

type IngredientSubstitutionModificationInput = {
  readonly addedIngredientId?: string;
  readonly removedIngredientId?: string;
};

type TelemetryLocationInput = {
  readonly id?: string;
  readonly googlePlaceId?: string;
  readonly name?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zipCode?: string;
};

type TelemetryProductInput = {
  readonly id?: string;
  readonly categoryId?: string;
  readonly restaurantId?: string;
  readonly calories?: number;
  readonly cost?: number;
  readonly name?: string;
  readonly location?: string;
  readonly quantity?: number;
  readonly featured?: boolean;
  readonly bread?: boolean;
  readonly dressingStyle?: string;
  readonly tags?: readonly string[];
  readonly ingredients?: ReadonlyArray<Ingredient | undefined>;
};
